<template>
  <form :class="{ 'form-grid': activeTab === 'general' }" :novalidate="true" @submit.prevent="save">
    <div class="form-tabs" :class="{ 'form-tabs-documents': activeTab === 'documents' }">
      <button
        type="button"
        class="form-tab"
        :class="{ 'form-tab--active': activeTab === 'general' }"
        @click="activeTab = 'general'"
      >
        Общая информация
      </button>
      <button
        type="button"
        class="form-tab"
        :class="{ 'form-tab--active': activeTab === 'documents' }"
        @click="activeTab = 'documents'"
      >
        Документы
      </button>
    </div>

    <div v-show="activeTab === 'general'" class="tab-content">
      <div class="form-grid__item">
        <Field label="Страна" required>
          <ComponentInput
            placeholder="Укажите страну"
            :select2Settings="select2Settings"
            :select2Options="countryList"
            v-model="item.countryId"
            v-on:select2OnSelect="changeCountry"
            type="select2"
            required
          />
        </Field>
      </div>

      <div class="form-grid__item">
        <Field label="Наименование поручения" required>
          <ComponentInput
            placeholder="Напишите текст вашего поручения"
            v-model="item.assignmentName"
            type="textarea"
            required
            mod="rectangular"
          />
        </Field>
      </div>

      <div class="form-grid__item form-grid form-grid--gutter-medium">
        <div class="form-grid__item form-grid__item--half">
          <Field label="Дата поступления" required>
            <DateComponent
              v-model="item.receiptDate"
              :clearable="true"
              required
              modClass="redesigned"
              :typeable="true"
              dateIcon
            />
          </Field>
        </div>

        <div class="form-grid__item form-grid__item--half">
          <Field label="Корреспондент" required>
            <SelectMultiComponent
              v-model="correspondentModel"
              :class="{ error: v$.item?.correspondent?.id?.$error }"
              :options="computedCorrespondentList"
              valueProp="id"
              labelOptions="text"
              trackBy="text"
              placeholder="Выберите"
              modifier="rectangular"
              required
            />
          </Field>
        </div>

        <div class="form-grid__item form-grid__item--half">
          <Field label="Срок исполнения" required>
            <DateComponent
              v-model="item.deadline"
              :clearable="true"
              required
              modClass="redesigned"
              :typeable="true"
              dateIcon
            />
          </Field>
        </div>

        <div class="form-grid__item form-grid__item--half">
          <Field label="Ответственный" required>
            <SelectMultiComponentCheckbox
              :class="{ error: v$.item?.responsibleEmployees?.$error }"
              v-model="responsibleEmployeesModel"
              placeholder="Выберите"
              trackBy="text"
              valueProp="id"
              labelOptions="text"
              :options="computedResponsibleList"
              required
            />
          </Field>
        </div>
      </div>

      <div class="form-grid__item form-grid__item--half">
        <Field label="Тип поручения" required>
          <SelectMultiComponent
            :key="`type-${multiselectKey}`"
            :class="{ error: v$.item?.typeId?.$error }"
            v-model="item.typeId"
            :options="typeList"
            valueProp="id"
            labelOptions="text"
            trackBy="text"
            placeholder="Выберите тип"
            modifier="rectangular"
            required
          />
        </Field>
      </div>

      <div class="form-grid__item" v-if="showOtherTypeField">
        <Field label="Другой тип поручения" required>
          <ComponentInput
            placeholder="Укажите другой тип поручения"
            v-model="item.otherType"
            type="input"
            mod="rectangular"
            required
          />
        </Field>
      </div>

      <div class="form-grid__item">
        <Field label="Связанное мероприятие/объект">
          <div class="form-grid form-grid--gutter-x-small">
            <div class="form-grid__item">
              <SelectMultiComponent
                :key="`event-type-${multiselectKey}`"
                v-model="item.eventTypeId"
                :options="eventTypeList"
                valueProp="id"
                labelOptions="text"
                trackBy="text"
                placeholder="Выберите тип объекта"
                modifier="rectangular"
                canClear
                @select="handleEventTypeSelect"
                @clear="handleEventTypeSelect"
              />
            </div>
            <div class="form-grid__item">
              <SelectMultiComponent
                :key="`event-${multiselectKey}`"
                placeholder="Укажите связанное мероприятие"
                openDirection="top"
                v-model="item.eventId"
                :options="eventFilterList"
                valueProp="id"
                labelOptions="text"
                trackBy="text"
                modifier="rectangular"
              />
            </div>
          </div>
        </Field>
      </div>

      <div v-if="item.eventTypeId === eventTypeList[0].id" class="form-grid__item">
        <ButtonComponent mod="gradient-bg" @click.prevent="addEvent">Создать мероприятие</ButtonComponent>
      </div>
    </div>

    <div v-show="activeTab === 'documents'" class="tab-content">
      <div v-for="(document, index) in assignmentFiles" :key="index" class="document-row">
        <div class="form-grid__item">
          <div class="document-fields form-grid form-grid--gutter-medium">
            <div class="form-grid__item form-grid__item--half">
              <Field label="Документ">
                <FileUpload
                  :entity="document"
                  propName="file"
                  label="Выберите файл +"
                  width="100%"
                  :required="false"
                  @uploaded="onFileUploaded($event, index)"
                  @deleteFile="onFileDelete($event, index)"
                  deleteInnerFileOnButton
                />
              </Field>
            </div>

            <div class="form-grid__item form-grid__item--half">
              <Field label="Описание документа">
                <ComponentInput
                  placeholder="Введите описание документа"
                  v-model="document.fileText"
                  type="input"
                  mod="rectangular"
                  :maxLength="100"
                />
              </Field>
            </div>
          </div>
        </div>
      </div>
      <div class="form-grid__caption">
        Возможна загрузка до 10 файлов в форматах: .docx, .xlsx, .pptx, .pdf, .jpeg, .png - размером не более 20 Мб
      </div>
      <div class="form-grid__item" v-if="assignmentFiles.length < 10">
        <ButtonComponent mod="gradient-bg" @click.prevent="addDocument">+ Добавить документ</ButtonComponent>
      </div>
    </div>

    <div class="form-grid__item" :class="{ 'document-button': activeTab === 'documents' }">
      <ButtonComponent mod="gradient-bg">
        {{ item.id > 0 ? 'Сохранить поручение' : 'Создать поручение' }}
      </ButtonComponent>
    </div>
  </form>
</template>

<script>
  // Additional
  import { useVuelidate } from '@vuelidate/core';
  import { required } from '@vuelidate/validators';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import DateComponent from '@/common/components/DateComponent/DateComponent.vue';
  import Field from '@/common/components/Field.vue';
  import FileUpload from '@/common/components/FileUpload.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import SelectMultiComponentCheckbox from '@/common/components/redesigned/SelectRedesigned/SelectMultiComponentCheckbox.vue';
  import SelectMultiComponent from '@/common/components/SelectMultiComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';

  import API from '../api/assignment';

  const PERMISSIONS = {
    create: Constants.Permissions.EapAssignmentCreate,
    edit: Constants.Permissions.EapAssignmentEdit,
  };

  export default {
    name: 'EapAssignmentCardAdd',

    components: {
      ButtonComponent,
      SelectMultiComponent,
      SelectMultiComponentCheckbox,
      DateComponent,
      ComponentInput,
      Field,
      FileUpload,
    },

    props: {
      nn: Number,
      id: Number,
      countryId: {
        type: Number,
        default: null,
      },
      projectId: {
        type: Number,
        default: null,
      },
    },

    mixins: [security],

    setup: () => ({ v$: useVuelidate() }),

    validations() {
      return {
        item: {
          typeId: { required },
          correspondent: {
            id: { required },
          },
          countryId: { required },
          responsibleEmployees: { required },
        },
      };
    },

    data() {
      return {
        activeTab: 'general',
        item: {
          assignmentFiles: [],
        },
        assignmentFiles: [],

        select2Settings: Constants.select2Settings,
        countryList: [],
        eventList: [],
        urgencyList: [],
        typeList: [],
        correspondentList: [],
        responsibleList: [],
        initResponsibleList: [],
        eventTypeList: [
          { id: 0, text: 'Мероприятие' },
          { id: 1, text: 'Проект' },
          { id: 2, text: 'МПК' },
        ],
        forceShowOtherType: false,
        multiselectKey: 0,
      };
    },

    async created() {
      const permission = this.id > 0 ? PERMISSIONS.edit : PERMISSIONS.create;
      const canMutate = await this.checkPermission(permission)
        .then((response) => response?.data)
        .catch(() => false);

      if (!canMutate) {
        this.$emit('close');
        return;
      }

      Utils.loadSelectOptions('countryList', this.countryList);
      Utils.loadSelectOptions('eapAssignmentTypeList', this.typeList);
      Utils.loadSelectOptions('eapAssignmentUrgencyList', this.urgencyList);

      this.fetchCorrespondents();
      this.fetchResponsibles();

      this.init();
    },

    methods: {
      init() {
        if (this.v$) {
          this.v$.$reset();
        }

        this.item = {
          countryId: this.countryId > 0 ? this.countryId : null,
          eventId: this.projectId > 0 ? this.projectId : null,
          eventTypeId: this.projectId > 0 ? 1 : null,
          typeId: null,
          urgencyId: null,
          correspondent: null,
          responsibleEmployees: null,
          receiptDate: null,
          deadline: null,
          otherType: null,
          assignmentFiles: [],
        };

        this.assignmentFiles = [
          {
            file: null,
            fileText: '',
          },
        ];

        this.eventList = [];
        this.forceShowOtherType = false;
        this.activeTab = 'general';

        if (this.id > 0) {
          API.find(this.id).then((response) => {
            const data = response.data;

            const savedEventTypeId = data.eventTypeId;
            const savedEventId = data.eventId;

            this.item = data;

            if (data.assignmentFiles && data.assignmentFiles.length > 0) {
              this.assignmentFiles = data.assignmentFiles.map((fileItem) => ({
                fileId: fileItem.fileId,
                file: fileItem.file,
                fileText: fileItem.fileText || '',
              }));
            } else {
              this.assignmentFiles = [
                {
                  file: null,
                  fileText: '',
                },
              ];
            }

            if (this.item.typeId === 5) {
              this.forceShowOtherType = true;
            }

            if (this.item.countryId > 0) {
              this.changeCountry(true).then(() => {
                this.$nextTick(() => {
                  this.item.eventTypeId = savedEventTypeId;
                  this.item.eventId = savedEventId;
                  this.forceMultiselectUpdate();
                });
              });
            } else {
              this.$nextTick(() => {
                this.item.eventTypeId = savedEventTypeId;
                this.item.eventId = savedEventId;
                this.forceMultiselectUpdate();
              });
            }

            this.initResponsibleList = this.item.responsibleEmployees;
          });
        } else if (this.countryId && this.projectId) {
          this.changeCountry(true);
        }
      },

      forceMultiselectUpdate() {
        this.multiselectKey += 1;
      },

      handleEventTypeSelect() {
        this.item.eventId = null;
        this.forceMultiselectUpdate();
      },

      async save() {
        this.v$.$touch();
        if (this.v$.$invalid) {
          Constants.alert.fire('Ошибка', 'Проверьте правильность заполнения полей', 'error');
          this.activeTab = 'general';
          return;
        }

        this.item.countryId = +this.item.countryId;
        const saveData = {
          ...this.item,
          assignmentFiles: this.assignmentFiles
            .filter((doc) => doc.file && doc.file.id)
            .map((doc) => ({
              fileId: doc.file.id,
              fileText: doc.fileText || '',
            })),
        };

        if (!saveData.urgencyId) delete saveData.urgencyId;

        try {
          const response = await API.update(saveData);
          Constants.alert.fire('Сохранение', Constants.commonSuccess, 'success');
          if (response && (response.data.id || this.item.id > 0)) {
            this.$emit('save', true);
          }
        } catch (error) {
          console.error('Ошибка сохранения:', error);
          Constants.alert.fire('Сохранение', Constants.commonError, 'error');
        }
      },

      async addEvent() {
        const result = await Constants.alert.fire({
          title: 'Подтверждение',
          text: 'Сохранить поручение и перейти на форму создания мероприятия?',
          confirmButtonText: 'Сохранить и перейти',
          cancelButtonText: 'Отмена',
          showCancelButton: true,
          icon: 'question',
        });

        if (!result.isConfirmed) {
          return;
        }

        this.v$.$touch();
        if (this.v$.$invalid) {
          Constants.alert.fire('Ошибка', 'Проверьте правильность заполнения полей', 'error');
          this.activeTab = 'general';
          return;
        }

        const saveData = {
          ...this.item,
          countryId: +this.item.countryId,
          assignmentFiles: this.assignmentFiles
            .filter((doc) => doc.file && doc.file.id)
            .map((doc) => ({
              fileId: doc.file.id,
              fileText: doc.fileText || '',
            })),
        };
        if (!saveData.urgencyId) delete saveData.urgencyId;

        try {
          const response = await API.update(saveData);
          if (response && (response.data.id || this.item.id > 0)) {
            await Constants.alert.fire('Сохранение', Constants.commonSuccess, 'success');
            this.$emit('save', true);

            const assignmentId = response.data?.id || this.item?.id;

            this.$router.push({
              path: '/mpk/events/add',
              query: {
                assignmentId: assignmentId,
                assignmentCountryId: this.item.countryId,
              },
            });
          } else {
            Constants.alert.fire('Сохранение', Constants.commonError, 'error');
          }
        } catch (error) {
          Constants.alert.fire('Сохранение', Constants.commonError, 'error');
        }
      },

      addDocument() {
        if (this.assignmentFiles.length < 10) {
          this.assignmentFiles.push({
            file: null,
            fileText: '',
          });
        }
      },

      onFileUploaded(event, index) {
        this.assignmentFiles[index].file = event.data;
      },

      onFileDelete(event, index) {
        if (this.assignmentFiles[index].file) {
          this.assignmentFiles[index].file = null;
          this.assignmentFiles[index].fileText = '';
        } else {
          if (this.assignmentFiles.length > 1) {
            this.assignmentFiles.splice(index, 1);
          } else {
            this.assignmentFiles[0] = {
              file: null,
              fileText: '',
            };
          }
        }
      },

      async changeCountry(notEmpty) {
        this.eventList = [];
        if (!notEmpty) this.item.eventId = null;

        await this.fetchEventList();
        this.forceMultiselectUpdate();
      },

      async fetchEventList() {
        if (!(this.item.countryId > 0)) {
          return;
        }
        this.eventList = await API.getEventForList({ countryId: this.item.countryId })
          .then((response) => response?.data)
          .catch(() => []);
      },

      async fetchCorrespondents() {
        this.correspondentList = await API.getCorrespondentsCandidates({
          countryId: this.item?.countryId ?? null,
        })
          .then((response) => response?.data)
          .catch(() => []);
      },

      async fetchResponsibles() {
        this.responsibleList = await API.getResponsibleCandidates({
          countryId: this.item?.countryId ?? null,
          showOnlyActive: true,
        })
          .then((response) => response?.data)
          .catch(() => []);
      },

      mapPersonData(person) {
        const position = person.position ? `(${person.position})` : '';
        return {
          id: Number(person.id),
          text: person.fullName + ' ' + position,
        };
      },
    },
    computed: {
      showOtherTypeField() {
        return this.item.typeId == 5 || this.forceShowOtherType;
      },

      eventFilterList() {
        var list = [];
        if (this.eventList) {
          this.eventList
            .filter((x) => x.eventType == this.item.eventTypeId)
            .map((x) => ({ id: x.eventId, text: x.eventName }))
            .forEach(function (el) {
              list.push(el);
            });
        }

        return list;
      },

      computedCorrespondentList() {
        return this.correspondentList.map(this.mapPersonData);
      },

      computedResponsibleList() {
        return this.responsibleList
          .filter(
            (responsibleItem) =>
              !this.initResponsibleList.some((initResponsibleItem) => initResponsibleItem.id == responsibleItem.id),
          )
          .concat(
            this.initResponsibleList.filter((initResponsibleItem) =>
              initResponsibleItem.countryIds.some(
                (responsibleCountryId) => responsibleCountryId == this.item.countryId,
              ),
            ),
          )
          .map(this.mapPersonData);
      },

      correspondentModel: {
        get() {
          return Number(this.item?.correspondent?.id);
        },

        set(id) {
          Object.assign(this.item, {
            correspondent: { id },
          });
        },
      },

      responsibleEmployeesModel: {
        get() {
          return this.item?.responsibleEmployees?.map?.((item) => item.id);
        },
        set(ids) {
          Object.assign(this.item, {
            responsibleEmployees: ids.map((id) => ({ id })),
          });
        },
      },
    },

    watch: {
      nn() {
        this.init();
      },

      'item.countryId'() {
        this.fetchCorrespondents();
        this.fetchResponsibles();
      },

      'item.typeId'(newVal) {
        if (newVal == 5) {
          this.forceShowOtherType = true;
        } else {
          this.forceShowOtherType = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  :deep(.label-input) {
    padding: 10px 16px 5px 16px;
  }
  .form-tabs {
    display: flex;
    border-bottom: 1px solid #e0e0e0;
    grid-column: span 2;
    &-documents {
      margin-bottom: 40px;
    }
  }

  .tab-content {
    grid-column: span 2;
    display: contents;
  }

  .form-tab {
    padding: 12px 24px;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    transition: all 0.3s ease;

    &:hover {
      color: #333;
    }

    &.form-tab--active {
      color: #1976d2;
      border-bottom-color: #1976d2;
    }
  }

  .document-row {
    margin-bottom: 5px;
    padding-bottom: 24px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }
  }

  .document-fields {
    width: 100%;
  }

  .document-button {
    margin-top: 40px;
  }

  .form-grid {
    display: grid;
    grid-row-gap: var(--form-grid-gutter, 40px);
    grid-column-gap: 24px;
    grid-template-columns: 1fr 1fr;

    &__caption {
      color: $gray-silver-dark;
      font-size: 0.95rem;
      margin-bottom: 24px;
    }
  }

  .form-grid--gutter-large {
    --form-grid-gutter: 40px;
  }

  .form-grid--gutter-medium {
    --form-grid-gutter: 24px;
  }

  .form-grid--gutter-small {
    --form-grid-gutter: 16px;
  }

  .form-grid--gutter-x-small {
    --form-grid-gutter: 8px;
  }

  .form-grid__item {
    grid-column: span 2;
  }

  .form-grid__item--half {
    grid-column: span 1;

    @media (max-width: $mobile) {
      grid-column: span 2;
    }
  }

  .form-grid__group {
    display: grid;
    grid-gap: 8px;
  }

  .error {
    border: 1px solid red;
  }
</style>
